/**
 * Per-chart / per-occasion detail rows for compliance XLSX exports.
 * Includes HN, dates, and other stored chart fields when the category has them.
 * nurse_notes uses a full form dump (chart title, cross-audit ward, split นิเทศ/ปัญหา,
 * checklist headers, readable ทำ/ไม่ทำ/อื่นๆ answers).
 */
import { getChecklist } from '../lib/checklists';
import { getCategoryById } from '../lib/complianceCategories';
import {
  filterSubmissionsForUi,
  resolveDepartmentDisplayName,
  resolveSubmissionDepartmentDisplayName,
} from '../lib/departmentUi';
import type { ReportingMonth } from '../lib/reportingMonth';
import type { ReportingSubmission } from '../services/dashboardData';
import {
  computeChartCountFromData,
  normalizeSubmissionShape,
} from '../services/submissionShape';
import { perChartConsistencyPctFromStrings } from '../lib/nursingComplianceScoring';
import { formatThaiMonthYYBuddhist } from './dateThai';

/** Fields exported as one cell per chart index (parallel arrays on the submission). */
export const CATEGORY_DETAIL_FIELD_WHITELIST: Record<string, readonly string[]> = {
  needleandsharp: ['dates', 'shifts', 'answers'],
  med_error: ['dates', 'answers'],
  siren_bundle: ['dates', 'hn', 'answers'],
  nurse_notes: [
    'chartTitles',
    'crossAuditByDeptIds',
    'dates',
    'disease',
    'focus',
    'treatments',
    'suggestions',
    'supervisionRecords',
    'problems',
    'answers',
  ],
};

const FIELD_LABELS: Record<string, string> = {
  dates: 'วันที่',
  hn: 'HN',
  shifts: 'เวร',
  disease: 'โรค',
  focus: 'จำนวน Focus list ที่สอดคล้องกับการรักษาของแพทย์',
  treatments: 'จำนวนการรักษาที่แพทย์บันทึก',
  problems: 'ปัญหาที่พบภาพรวม',
  suggestions: 'ข้อเสนอแนะอื่นๆ',
  supervisionRecords: 'รายการนิเทศ',
  chartTitles: 'ชื่อแผนภูมิ',
  crossAuditByDeptIds: 'หน่วยงานที่ตรวจไขว้',
};

const SHIFT_LABELS: Record<string, string> = {
  morning: 'เช้า',
  evening: 'บ่าย',
  night: 'ดึก',
};

const NURSE_NOTES_ANSWER_LABELS: Record<string, string> = {
  '1': 'ทำ',
  '0': 'ไม่ทำ',
  NA: 'อื่นๆ',
};

const CHART_INDEX_LABEL: Record<string, string> = {
  siren_bundle: 'ลำดับ HN',
  needleandsharp: 'ครั้งที่',
  med_error: 'สัปดาห์',
  nurse_notes: 'แผนภูมิ',
};

const SUPERVISION_COLUMN_SPECS = [
  { key: 'recipients', label: 'ผู้รับการนิเทศ' },
  { key: 'recipientsDetail', label: 'ระบุผู้รับ' },
  { key: 'activities', label: 'กิจกรรม' },
  { key: 'issues', label: 'ประเด็น' },
  { key: 'issueOther', label: 'ประเด็นอื่นๆ' },
  { key: 'problemDetail', label: 'รายละเอียดปัญหา' },
  { key: 'solution', label: 'การแก้ไข' },
  { key: 'followUp', label: 'การติดตาม' },
] as const;

type SupervisionRecord = {
  recipients?: string[];
  recipientsDetail?: string;
  activities?: string[];
  issues?: string[];
  issueOther?: string;
  problemDetail?: string;
  solution?: string;
  followUp?: string;
};

function canonicalCategoryKey(categoryId: string): string {
  return getCategoryById(categoryId)?.id ?? categoryId;
}

function detailFieldsForCategory(categoryId: string): readonly string[] {
  const key = canonicalCategoryKey(categoryId);
  return CATEGORY_DETAIL_FIELD_WHITELIST[key] ?? CATEGORY_DETAIL_FIELD_WHITELIST[categoryId] ?? [];
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function isFirestoreNestedArray(
  value: unknown,
): value is { firestoreNestedArray: true; items: unknown[] } {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    (value as { firestoreNestedArray?: unknown }).firestoreNestedArray === true &&
    Array.isArray((value as { items?: unknown }).items)
  );
}

function unwrapExportValue(value: unknown): unknown {
  if (isFirestoreNestedArray(value)) return value.items;
  return value;
}

function cellAt(source: unknown, index: number): unknown {
  if (!Array.isArray(source)) return undefined;
  return source[index];
}

function formatPrimitive(value: unknown, field?: string): string {
  const unwrapped = unwrapExportValue(value);
  if (unwrapped == null) return '—';
  if (field === 'shifts' && typeof unwrapped === 'string') {
    return SHIFT_LABELS[unwrapped] ?? unwrapped;
  }
  if (typeof unwrapped === 'string') {
    const trimmed = unwrapped.trim();
    return trimmed || '—';
  }
  if (typeof unwrapped === 'number' || typeof unwrapped === 'boolean') return String(unwrapped);
  if (Array.isArray(unwrapped)) {
    const joined = unwrapped
      .map((item) => formatPrimitive(item, field))
      .filter((item) => item !== '—')
      .join(', ');
    return joined || '—';
  }
  if (typeof unwrapped === 'object') {
    // Avoid "[object Object]" in exports; prefer readable nested text when present.
    const record = asRecord(unwrapped);
    const preferred = ['text', 'detail', 'value', 'label', 'message']
      .map((key) => record[key])
      .find((item) => typeof item === 'string' && item.trim());
    if (typeof preferred === 'string') return preferred.trim();
    return '—';
  }
  return String(unwrapped);
}

function coerceDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.valueOf()) ? null : value;
  if (typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.valueOf()) ? null : parsed;
  }
  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    const maybe = value as { toDate?: () => Date };
    if (typeof maybe.toDate === 'function') {
      try {
        const date = maybe.toDate();
        return date instanceof Date && !Number.isNaN(date.valueOf()) ? date : null;
      } catch {
        return null;
      }
    }
  }
  return null;
}

function formatDateValue(value: unknown): string {
  if (Array.isArray(value)) {
    const first = value.find((item) => item != null && String(item).trim() !== '');
    return formatDateValue(first);
  }
  const date = coerceDate(value);
  if (date) return date.toLocaleDateString('th-TH', { dateStyle: 'medium' });
  return formatPrimitive(value, 'dates');
}

function formatHnValue(value: unknown): string {
  if (Array.isArray(value)) {
    const first = value.find((item) => item != null && String(item).trim() !== '');
    return formatHnValue(first);
  }
  return formatPrimitive(value, 'hn');
}

function formatAnswerCell(value: unknown): string {
  if (value == null) return '—';
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || '—';
  }
  if (Array.isArray(value)) {
    const first = value.find((item) => item != null && String(item).trim() !== '');
    return formatAnswerCell(first);
  }
  return String(value);
}

/** Map nurse_notes checklist codes to Thai labels used in the form (1=ทำ, 0=ไม่ทำ, NA=อื่นๆ). */
export function formatNurseNotesAnswerLabel(value: unknown): string {
  if (value == null) return '—';
  if (Array.isArray(value)) {
    const first = value.find((item) => item != null && String(item).trim() !== '');
    return formatNurseNotesAnswerLabel(first);
  }
  const raw = String(value).trim();
  if (!raw) return '—';
  return NURSE_NOTES_ANSWER_LABELS[raw] ?? raw;
}

function formatSupervisionField(record: SupervisionRecord, key: (typeof SUPERVISION_COLUMN_SPECS)[number]['key']): string {
  const value = record[key];
  if (Array.isArray(value)) {
    const joined = value.map((item) => String(item ?? '').trim()).filter(Boolean).join(', ');
    return joined || '—';
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || '—';
  }
  return '—';
}

function formatSupervisionColumns(value: unknown): string[] {
  const record = asRecord(value) as SupervisionRecord;
  return SUPERVISION_COLUMN_SPECS.map((spec) => formatSupervisionField(record, spec.key));
}

function checklistAnswerLabels(categoryId: string): string[] {
  const category = getCategoryById(categoryId);
  const sections = getChecklist(category?.title ?? categoryId);
  if (!sections.length) return [];
  const includeSectionTitle = sections.length > 1;
  return sections.flatMap((section) =>
    section.items.map((item) =>
      section.title && includeSectionTitle ? `${section.title}: ${item}` : item,
    ),
  );
}

function answerColumnCount(categoryId: string, submissions: ReportingSubmission[]): number {
  const fromChecklist = checklistAnswerLabels(categoryId).length;
  let maxFromData = 0;
  submissions.forEach((submission) => {
    const shape = normalizeSubmissionShape(submission as Record<string, unknown>);
    shape.answers.forEach((row) => {
      if (Array.isArray(row)) maxFromData = Math.max(maxFromData, row.length);
    });
  });
  return Math.max(fromChecklist, maxFromData, 0);
}

function chartIndexLabel(categoryId: string): string {
  const key = canonicalCategoryKey(categoryId);
  return CHART_INDEX_LABEL[key] ?? CHART_INDEX_LABEL[categoryId] ?? 'แผนภูมิ';
}

function chartHasContent(
  fields: readonly string[],
  raw: Record<string, unknown>,
  shapeAnswers: string[][],
  chartIndex: number,
): boolean {
  return fields.some((field) => {
    if (field === 'answers') {
      const row = shapeAnswers[chartIndex];
      return Array.isArray(row) && row.some((cell) => String(cell ?? '').trim() !== '');
    }
    if (field === 'crossAuditByDeptIds') {
      const fromPrimary = cellAt(raw.crossAuditByDeptIds, chartIndex);
      const fromFallback = cellAt(raw.crossAuditTargetDeptIds, chartIndex);
      const value = fromPrimary ?? fromFallback;
      return value != null && String(value).trim() !== '';
    }
    const value = cellAt(raw[field], chartIndex);
    if (value == null) return false;
    if (typeof value === 'string') return value.trim() !== '';
    if (Array.isArray(value)) return value.some((item) => String(item ?? '').trim() !== '');
    if (typeof value === 'object') return Object.keys(asRecord(value)).length > 0;
    return String(value).trim() !== '';
  });
}

function resolveCrossAuditDepartmentName(
  raw: Record<string, unknown>,
  chartIndex: number,
  deptIdToName: Map<string, string>,
): string {
  const id =
    formatPrimitive(cellAt(raw.crossAuditByDeptIds, chartIndex)) !== '—'
      ? String(cellAt(raw.crossAuditByDeptIds, chartIndex) ?? '').trim()
      : String(cellAt(raw.crossAuditTargetDeptIds, chartIndex) ?? '').trim();
  if (!id) return '—';
  return resolveDepartmentDisplayName(id, null, deptIdToName);
}

function problemColumnCount(submissions: ReportingSubmission[], answerCount: number): number {
  let max = answerCount;
  submissions.forEach((submission) => {
    const problems = (submission as Record<string, unknown>).problems;
    if (!Array.isArray(problems)) return;
    problems.forEach((row) => {
      const unwrapped = unwrapExportValue(row);
      if (Array.isArray(unwrapped)) max = Math.max(max, unwrapped.length);
    });
  });
  return Math.max(max, 0);
}

function buildNurseNotesDetailExportData(params: SubmissionDetailExportParams): {
  headers: string[];
  rows: string[][];
} {
  const submissions = filterSubmissionsForUi(params.submissions);
  const answerCount = answerColumnCount('nurse_notes', submissions);
  const checklistLabels = checklistAnswerLabels('nurse_notes');
  const answerHeaders =
    answerCount > 0
      ? Array.from({ length: answerCount }, (_, index) => checklistLabels[index] ?? `ข้อ${index + 1}`)
      : [];
  const problemCount = problemColumnCount(submissions, answerCount);
  const problemHeaders =
    problemCount > 0
      ? Array.from({ length: problemCount }, (_, index) => `ปัญหาข้อ${index + 1}`)
      : [];

  const headers = [
    'หน่วยงาน',
    'เดือนรายงาน',
    chartIndexLabel('nurse_notes'),
    'ผู้ส่ง',
    'ชื่อแผนภูมิ',
    'หน่วยงานที่ตรวจไขว้',
    'วันที่',
    'โรค',
    FIELD_LABELS.focus,
    FIELD_LABELS.treatments,
    'ร้อยละความสอดคล้องรายแผนภูมิ',
    FIELD_LABELS.suggestions,
    ...SUPERVISION_COLUMN_SPECS.map((spec) => spec.label),
    ...problemHeaders,
    ...answerHeaders,
  ];

  const fields = detailFieldsForCategory('nurse_notes');
  const rows: string[][] = [];

  submissions.forEach((submission) => {
    const raw = submission as Record<string, unknown>;
    const shape = normalizeSubmissionShape(raw);
    const chartCount = Math.max(
      1,
      computeChartCountFromData(raw, shape.answers.length || 1),
      shape.focus.length,
      shape.treatments.length,
      shape.chartTitles.length,
      ...fields.map((field) => (Array.isArray(raw[field]) ? (raw[field] as unknown[]).length : 0)),
      Array.isArray(raw.crossAuditByDeptIds) ? raw.crossAuditByDeptIds.length : 0,
      Array.isArray(raw.crossAuditTargetDeptIds) ? raw.crossAuditTargetDeptIds.length : 0,
    );

    for (let chartIndex = 0; chartIndex < chartCount; chartIndex += 1) {
      if (!chartHasContent(fields, raw, shape.answers, chartIndex)) {
        // Still export rows that only have focus/treatments from charts[] (shape).
        const hasShapeFocus = String(shape.focus[chartIndex] ?? '').trim() !== '';
        const hasShapeTreatments = String(shape.treatments[chartIndex] ?? '').trim() !== '';
        if (!hasShapeFocus && !hasShapeTreatments) continue;
      }

      const reportingMonth =
        typeof submission.reportingMonth === 'string' && submission.reportingMonth
          ? formatThaiMonthYYBuddhist(submission.reportingMonth as ReportingMonth)
          : '—';

      const problemRaw = unwrapExportValue(cellAt(raw.problems, chartIndex));
      const problemRow = Array.isArray(problemRaw) ? problemRaw : [];
      const problemCells =
        problemCount > 0
          ? Array.from({ length: problemCount }, (_, problemIndex) =>
              formatPrimitive(problemRow[problemIndex]),
            )
          : [];

      const answerRow = shape.answers[chartIndex] ?? [];
      const answerCells =
        answerCount > 0
          ? Array.from({ length: answerCount }, (_, answerIndex) =>
              formatNurseNotesAnswerLabel(answerRow[answerIndex]),
            )
          : [];

      const focusValue =
        String(shape.focus[chartIndex] ?? '').trim() ||
        formatPrimitive(cellAt(raw.focus, chartIndex), 'focus');
      const treatmentsValue =
        String(shape.treatments[chartIndex] ?? '').trim() ||
        formatPrimitive(cellAt(raw.treatments, chartIndex), 'treatments');
      const focusCell = focusValue && focusValue !== '—' ? focusValue : '—';
      const treatmentsCell = treatmentsValue && treatmentsValue !== '—' ? treatmentsValue : '—';
      const consistencyPct = perChartConsistencyPctFromStrings(
        focusCell === '—' ? '' : focusCell,
        treatmentsCell === '—' ? '' : treatmentsCell,
      );

      const chartTitle =
        formatPrimitive(shape.chartTitles[chartIndex]) !== '—'
          ? formatPrimitive(shape.chartTitles[chartIndex])
          : formatPrimitive(cellAt(raw.chartTitles, chartIndex));

      rows.push([
        resolveSubmissionDepartmentDisplayName(submission, params.deptIdToName),
        reportingMonth,
        String(chartIndex + 1),
        submission.submittedByName?.trim() ||
          submission.userDisplayName?.trim() ||
          '—',
        chartTitle,
        resolveCrossAuditDepartmentName(raw, chartIndex, params.deptIdToName),
        formatDateValue(cellAt(raw.dates, chartIndex)),
        formatPrimitive(cellAt(raw.disease, chartIndex), 'disease'),
        focusCell,
        treatmentsCell,
        consistencyPct === null ? '—' : String(consistencyPct),
        formatPrimitive(cellAt(raw.suggestions, chartIndex), 'suggestions'),
        ...formatSupervisionColumns(cellAt(raw.supervisionRecords, chartIndex)),
        ...problemCells,
        ...answerCells,
      ]);
    }
  });

  return { headers, rows };
}

export type SubmissionDetailExportParams = {
  categoryId: string;
  submissions: ReportingSubmission[];
  deptIdToName: Map<string, string>;
};

/**
 * One row per chart/occasion with category detail fields (HN, date, answers, …).
 * Categories without a whitelist return empty headers/rows.
 */
export function buildSubmissionDetailExportData(
  params: SubmissionDetailExportParams,
): { headers: string[]; rows: string[][] } {
  if (canonicalCategoryKey(params.categoryId) === 'nurse_notes') {
    return buildNurseNotesDetailExportData(params);
  }

  const fields = detailFieldsForCategory(params.categoryId);
  if (!fields.length) {
    return { headers: [], rows: [] };
  }

  const submissions = filterSubmissionsForUi(params.submissions);
  const answerCount = fields.includes('answers')
    ? answerColumnCount(params.categoryId, submissions)
    : 0;
  const answerHeaders =
    answerCount > 0
      ? Array.from({ length: answerCount }, (_, index) => `ข้อ${index + 1}`)
      : [];

  const scalarFields = fields.filter((field) => field !== 'answers');
  const headers = [
    'หน่วยงาน',
    'เดือนรายงาน',
    chartIndexLabel(params.categoryId),
    'ผู้ส่ง',
    ...scalarFields.map((field) => FIELD_LABELS[field] ?? field),
    ...answerHeaders,
  ];

  const rows: string[][] = [];

  submissions.forEach((submission) => {
    const raw = submission as Record<string, unknown>;
    const shape = normalizeSubmissionShape(raw);
    const chartCount = Math.max(
      1,
      computeChartCountFromData(raw, shape.answers.length || 1),
      ...fields.map((field) => (Array.isArray(raw[field]) ? (raw[field] as unknown[]).length : 0)),
    );

    for (let chartIndex = 0; chartIndex < chartCount; chartIndex += 1) {
      if (!chartHasContent(fields, raw, shape.answers, chartIndex)) continue;

      const scalarCells = scalarFields.map((field) => {
        const value = cellAt(raw[field], chartIndex);
        if (field === 'dates') return formatDateValue(value);
        if (field === 'hn') return formatHnValue(value);
        return formatPrimitive(value, field);
      });

      const answerRow = shape.answers[chartIndex] ?? [];
      const answerCells =
        answerCount > 0
          ? Array.from({ length: answerCount }, (_, answerIndex) =>
              formatAnswerCell(answerRow[answerIndex]),
            )
          : [];

      const reportingMonth =
        typeof submission.reportingMonth === 'string' && submission.reportingMonth
          ? formatThaiMonthYYBuddhist(submission.reportingMonth as ReportingMonth)
          : '—';

      rows.push([
        resolveSubmissionDepartmentDisplayName(submission, params.deptIdToName),
        reportingMonth,
        String(chartIndex + 1),
        submission.submittedByName?.trim() ||
          submission.userDisplayName?.trim() ||
          '—',
        ...scalarCells,
        ...answerCells,
      ]);
    }
  });

  return { headers, rows };
}
